<template>
  <question-base-wrapper v-bind="baseWrapperProps">
    <validation-provider
      v-slot="{ errors, failed, failedRules }"
      :data-unit-test="`${stepId}_${questionId}`"
      :vid="`${stepId}_${questionId}`"
      :name="questionId"
      :rules="fieldValidationRules"
    >
      <input
        type="hidden"
        :value="groupList.qty"
      >
      <div
        v-if="failed && errors && errors.length"
        class="error--text fz12"
      >
        <p>{{ getFieldErrors('field', failedRules, errors)[0] }}</p>
      </div>
    </validation-provider>
    <transition
      name="fade-in"
    >
      <ul class="group-list">
        <li
          v-for="category in groupList.categories"
          :key="category.value"
          class="category"
          :class="{
            'is-open': category.isOpen,
          }"
        >
          <button
            class="opener"
            @click.prevent="category.isOpen = !category.isOpen"
          >
            <span class="opener-text">{{ category.text }}
              <template v-if="selectedInCategoryQty[category.value]"> ({{ selectedInCategoryQty[category.value] }})</template>
            </span>
            <v-icon
              class="opener-icon"
              color="primary"
            >
              mdi-chevron-down
            </v-icon>
          </button>
          <transition name="slide-fade">
            <ul
              v-if="category.isOpen"
              class="content "
            >
              <li
                v-for="(text, key) in groupList.data[category.value]"
                :key="`${category.value}-${key}`"
                class="item"
              >
                <checkbox
                  :checked="isChecked(key)"
                  :data-test="snakeCase(key)"
                  @change="onChange($event, key)"
                >
                  {{ text }}
                </checkbox>
              </li>
            </ul>
          </transition>
        </li>
      </ul>
    </transition>
  </question-base-wrapper>
</template>

<script>
import QuestionBase from './Base';
import QuestionBaseWrapper from './BaseWrapper';

export default {
  name: 'question-base-checkbox-list',
  extends: QuestionBase,
  components: { QuestionBaseWrapper },
  props: {
    isExpanded: {
      type: Boolean,
      default: true,
    },
    minValue: {
      type: Number,
      default: 1,
    },
    isGroupArray: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      fields: this.customFields,
      groupList: {
        categories: null,
        data: null,
        qty: 0,
      },
    };
  },
  computed: {
    selectedInCategoryQty () {
      const result = {};

      const fieldData = this.isGroupArray
        ? this.questionData?.[this.questionFields.field]
        : this.questionData;

      this.groupList.categories.forEach(category => {
        const qty = Object.keys(this.groupList.data[category.value]).reduce((acc, value) => {
          if (fieldData?.[value]) return acc + 1;
          return acc;
        }, 0);
        if (qty === 0) return;
        result[category.value] = qty;
      });
      return result;
    },
    fieldValidationRules () {
      return this.validationRules ? this.validationRules.field : 'required';
    },
    watchedField () {
      return this.stepData[this.questionFields.field];
    },
  },
  async created () {
    await this.loadGroupList();
    this.setupFields();
  },
  methods: {
    setupFields () {
      if (!this.isGroupArray) {
        this.groupList.qty = this.customFields.reduce((qty, field) => {
          this.questionData[field] = this.questionData[field] ?? false;
          return qty + (this.questionData[field] ? 1 : 0);
        }, 0);
        return;
      }

      if (this.questionData[this.questionFields.field] === null || Object.keys(this.questionFields.field).length === 0) {
        return;
      }

      this.groupList.qty = Object.keys(this.questionFields.field).length;
    },
    loadGroupList () {
      this.groupList.data = this.optionsConfig.options;
      this.groupList.categories = Object.keys(this.groupList.data).map(key => ({
        text: key,
        value: key,
        isOpen: this.isExpanded,
      }));
    },
    isChecked (key) {
      const fieldData = this.isGroupArray
        ? this.questionData?.[this.questionFields.field]
        : this.questionData;

      return !!fieldData?.[key];
    },
    onChange (e, key) {
      if (this.isGroupArray) {
        const fieldData = this.questionData[this.questionFields.field];
        fieldData[key]
          ? this.$delete(fieldData, key)
          : this.$set(fieldData, key, 1);
      } else {
        this.questionData[key] = e;
        this.groupList.qty = Object.values(this.selectedInCategoryQty).reduce((acc, val) => acc + val, 0);
      }
    },
    updateGroupListQty () {
      const fieldData = this.stepData[this.questionFields.field];

      if (fieldData) {
        const newQty = Object.keys(fieldData).length;
        if (this.groupList.qty !== newQty) {
          this.groupList.qty = newQty;
        }
      }
    },
    initializeQuestionData () {
      if (!this.questionData[this.questionFields.field]) {
        this.$set(this.questionData, this.questionFields.field, {});
      }
    },
  },
  watch: {
    watchedField: {
      handler () {
        if (this.isGroupArray) {
          this.updateGroupListQty();
          this.initializeQuestionData();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.group-list {
  padding: 0;

  .category + .category {
    border-top: 1px solid #d0d1d8;
  }

  .content {
    display: flex;
    flex-wrap: wrap;
    will-change: transform, opacity;

    .item {
      margin: 0 16px 16px 0;
    }
  }

  .opener {
    display: flex;
    width: 100%;
    color: currentColor;
    text-decoration: none;
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    padding: 18px 20px 18px 12px;

    & + * { margin-top: 16px; }

    &-icon { margin-left: auto; }

    &-text {
      text-decoration: underline;
      margin-right: auto;
    }

    &:hover {
      background-color: rgba(var(--v-primary-rgb, $clr-primary-rgb), 0.05);

      span { text-decoration: none; }
    }
  }

  .category.is-open .opener-icon {
    transform: rotate(180deg);
  }
}

.flat-list {
  :not(:first-child).item {
    margin-top: 12px;
  }
}
</style>
